import {Component, OnDestroy, OnInit} from '@angular/core';
import {debounceTime, Subject, takeUntil} from 'rxjs';
import {DropdownOption} from '../../../../../../models/dropdown-option.model';
import * as Validation
  from '../../../../../shared/constants/validation.constants';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {Store} from '@ngrx/store';
import * as fromRoot from '../../../../../../state/app.state';
import {Router} from '@angular/router';
import {CommonService} from '../../../../../shared/services/common.service';
import {
  FetchClinicFilterRequest,
  FetchGroupListFilterRequest,
  FetchStatusFilterRequest,
  SaveConfigId
} from '../../../core/clients-list.actions';
import {
  clinicFilter,
  groupListFilter,
  statusFilter
} from '../../../core/clients-list.selectors';
import {
  participantConfig
} from '../../../../../role-modules/participant/sidenav/sidenav.config';
import {Filter} from '../../../../../../models/filter';
import {roles} from '../../../../../shared/constants/common.constants';
import {ClientListService} from '../../../services/client-list.service';

@Component({
  selector: 'app-participant-behaviour-information-filter',
  templateUrl: './participant-behaviour-information-filter.component.html',
  styleUrls: ['./participant-behaviour-information-filter.component.css']
})
export class ParticipantBehaviourInformationFilterComponent implements OnInit,
  OnDestroy {
  loading: boolean = false;
  statusFilterList: DropdownOption[] = [];
  errorMessages: any = {};
  validation = Validation;
  errorMessageMap = {
    'clientId': {
      required: 'This field is required.'
    },
    'groupId': {
      required: 'This field is required.'
    },
    'role': {
      required: 'This field is required.'
    },
    'session': {
      required: 'This field is required.'
    },
    'status': {
      required: 'This field is required.'
    },
    'searchText': {
      required: 'This field is required.'
    }
  }
  clientOptions: Filter = {
    list: [],
    total: 0,
    pageNumber: 1,
    limit: 10,
    hasNext: false,
    hasPrev: false,
    loading: false
  };
  groupOptions: Filter = {
    list: [],
    total: 0,
    pageNumber: 1,
    limit: 10,
    hasNext: false,
    hasPrev: false,
    loading: false
  };

  roleFilterList: DropdownOption[] = [
    {
      key: roles.adult,
      value: 'Adult'
    },
    {
      key: roles.participant,
      value: 'Participant'
    }
  ];

  sessionFilterList: DropdownOption[] = [
    {
      key: 1,
      value: 'Up to S26'
    },
    {
      key: 2,
      value: 'After S26'
    }
  ];

  form = new FormGroup({
    clientId: new FormControl(null, {
      validators: [
        Validators.required
      ]
    }),
    groupId: new FormControl({value: null, disabled: true}, {
      validators: [
        Validators.required
      ]
    }),
    role: new FormControl([roles.participant]),
    session: new FormControl({value: 1, disabled: false}, {
      validators: [
        Validators.required
      ]
    }),
    status: new FormControl(null),
    searchText: new FormControl(null),
    searchClient: new FormControl(''),
    searchGroup: new FormControl('')
  })
  config = [
    {
      id: 1,
      label: 'Red Light Foods',
      isSelected: true
    },
    {
      id: 2,
      label: 'Physical Activity',
      isSelected: false
    },
    {
      id: 3,
      label: 'Screen Time',
      isSelected: false
    }

  ]
  protected readonly participantConfig = participantConfig;
  private readonly OnDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private router: Router,
    private commonService: CommonService,
    private clientListService: ClientListService
  ) {
    this.store.dispatch(FetchStatusFilterRequest());
    this.fetchClientsFilterList();
  }

  ngOnInit() {
    // EXTRACT VALUE FROM FORM//
    const {searchClient, searchGroup, clientId, ...filters} = this.form.getRawValue();
    // @ts-ignore
    this.clientListService.onParticipantBehaviourInformationFilterChange.next(filters);

    this.store.select(statusFilter)
      .pipe(takeUntil(this.OnDestroy))
      .subscribe((data) => {
        this.loading = data.loading;
        this.statusFilterList = data.list;
      });

    this.store.select(clinicFilter)
      .pipe(takeUntil(this.OnDestroy))
      .subscribe((data) => {
        this.clientOptions.list.push(...data?.list || []);
        this.clientOptions.hasNext = data?.hasNext
        this.clientOptions.loading = data?.loading;
      });

    this.form.controls.searchClient.valueChanges
      .pipe(debounceTime(1000), takeUntil(this.OnDestroy))
      .subscribe(() => {
        this.clientOptions.pageNumber = 1;
        this.clientOptions.list = [];
        this.fetchClientsFilterList();
      });

    this.store.select(groupListFilter)
      .pipe(takeUntil(this.OnDestroy))
      .subscribe((data) => {
        this.groupOptions.list.push(...data?.list || []);
        this.groupOptions.hasNext = data?.hasNext
        this.groupOptions.loading = data?.loading;
      });

    this.form.controls.searchGroup.valueChanges
      .pipe(debounceTime(1000), takeUntil(this.OnDestroy))
      .subscribe(() => {
        this.groupOptions.pageNumber = 1;
        this.groupOptions.list = [];
        this.fetchGroupFilterList();
      });

    this.form.controls.clientId.valueChanges
      .pipe(takeUntil(this.OnDestroy))
      .subscribe(data => {
        if (data) {
          this.groupOptions.pageNumber = 1;
          this.groupOptions.list = [];
          this.fetchGroupFilterList(data);
          this.form.controls.groupId.enable();
        }
      });
  }

  apply() {
    // ERROR CHECK IN THE FORM//
    if (this.form.invalid) {
      this.checkForErrors();
      return;
    }
    if (!this.form.dirty) { // check is there any change
      return;
    }
    // changes just applied, so marking the form as un-dirty to avoid applying same filters again without changing
    // anything
    this.form.markAsPristine();

    // EXTRACT VALUE FROM FORM//
    const {searchClient, searchGroup, clientId, ...filters} = this.form.getRawValue();
    // @ts-ignore
    this.clientListService.onParticipantBehaviourInformationFilterChange.next(filters)
  }

  checkForErrors(currentField ?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField))
    };
  }

  select(configId) {
    this.config.map((e) => {
      e.isSelected = e.id === configId;
    });
    this.store.dispatch(SaveConfigId({configId}));
  }

  fetchClientsFilterList() {
    const payload: any = {
      pageNumber: this.clientOptions?.pageNumber,
      limit: this.clientOptions?.limit,
      shouldInclude: this.form.value.clientId
    };
    if (this.form.controls?.searchClient?.value.trim()) {
      payload.search = this.form.controls.searchClient.value;
    }
    this.store.dispatch(FetchClinicFilterRequest({payload}));
  }

  clientsOnNextPage() {
    this.clientOptions.pageNumber = this.clientOptions.pageNumber + 1
    this.fetchClientsFilterList();
  }

  fetchGroupFilterList(clientId?) {
    const payload: any = {
      pageNumber: this.groupOptions?.pageNumber,
      limit: this.groupOptions?.limit,
      shouldInclude: this.form.value.groupId
    };
    if (this.form.controls?.searchGroup?.value.trim()) {
      payload.search = this.form.controls.searchGroup.value;
    }
    if (clientId) {
      payload.clientId = clientId;
    }
    this.store.dispatch(FetchGroupListFilterRequest({payload}));
  }

  groupOnNextPage() {
    this.groupOptions.pageNumber = this.groupOptions.pageNumber + 1
    this.fetchGroupFilterList();
  }

  ngOnDestroy() {
    this.OnDestroy.next(null);
    this.OnDestroy.complete();
  }
}
